'use client';

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useRouter } from 'next/navigation';
import { Eye, EyeOff, Loader2, Copy, CheckCheck } from 'lucide-react';
import { toast } from 'sonner';
import type { AuthMode } from './AuthShell';

interface LoginFields {
  email: string;
  password: string;
  remember: boolean;
}

interface SignupFields {
  fullName: string;
  email: string;
  organization: string;
  role: string;
  password: string;
  confirmPassword: string;
  terms: boolean;
}

interface Props {
  mode: AuthMode;
  setMode: (m: AuthMode) => void;
}

const mockCredentials = [
  { role: 'CEO', email: 'amara.osei@nhc-hospital.org', password: 'Halo@NHC2026' },
  { role: 'CFO', email: 'diana.mensah@nhc-hospital.org', password: 'Halo@NHC2026' },
  { role: 'Ministry', email: 'k.asante@moh.gov.gh', password: 'Halo@MOH2026' },
];

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };
  return (
    <button
      onClick={handleCopy}
      className="p-1 rounded transition-colors hover:bg-white/5"
      style={{ color: 'hsl(215 20% 65%)' }}
      type="button"
    >
      {copied ? <CheckCheck size={12} style={{ color: 'hsl(142 71% 45%)' }} /> : <Copy size={12} />}
    </button>
  );
}

export default function AuthForm({ mode, setMode }: Props) {
  const router = useRouter();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);

  const loginForm = useForm<LoginFields>({
    defaultValues: { email: '', password: '', remember: false },
  });

  const signupForm = useForm<SignupFields>({
    defaultValues: {
      fullName: '',
      email: '',
      organization: '',
      role: '',
      password: '',
      confirmPassword: '',
      terms: false,
    },
  });

  const handleLoginSubmit = (data: LoginFields) => {
    const valid = mockCredentials.find(
      (c) => c.email === data.email && c.password === data.password
    );

    if (!valid) {
      toast.error('Invalid credentials — use the demo accounts below to sign in');
      return;
    }

    setLoading(true);
    // TODO: Backend integration — POST /api/auth/login
    setTimeout(() => {
      setLoading(false);
      toast.success(`Welcome back, ${valid.role}`);
      router.push('/hospital-command-center-dashboard');
    }, 1200);
  };

  const handleSignupSubmit = (data: SignupFields) => {
    if (data.password !== data.confirmPassword) {
      signupForm.setError('confirmPassword', { message: 'Passwords do not match' });
      return;
    }
    setLoading(true);
    // TODO: Backend integration — POST /api/auth/signup
    setTimeout(() => {
      setLoading(false);
      toast.success('Account created — redirecting to dashboard');
      router.push('/hospital-command-center-dashboard');
    }, 1400);
  };

  const autofill = (cred: { email: string; password: string }) => {
    loginForm.setValue('email', cred.email);
    loginForm.setValue('password', cred.password);
  };

  return (
    <div className="animate-fade-in">
      {/* Mode toggle */}
      <div
        className="flex rounded-xl p-1 mb-8"
        style={{ background: 'hsl(220 33% 12%)', border: '1px solid hsl(220 20% 18%)' }}
      >
        {(['login', 'signup'] as AuthMode[]).map((m) => (
          <button
            key={`mode-${m}`}
            onClick={() => setMode(m)}
            className="flex-1 py-2.5 text-sm font-600 rounded-lg transition-all duration-200"
            style={{
              background: mode === m ? 'hsl(160 60% 68% / 0.12)' : 'transparent',
              color: mode === m ? 'hsl(160 60% 68%)' : 'hsl(215 20% 65%)',
            }}
          >
            {m === 'login' ? 'Sign In' : 'Create Account'}
          </button>
        ))}
      </div>

      <div className="mb-6">
        <h2 className="text-2xl font-700" style={{ color: 'hsl(210 40% 96%)' }}>
          {mode === 'login' ? 'Welcome back' : 'Get started'}
        </h2>
        <p className="text-sm mt-1" style={{ color: 'hsl(215 20% 65%)' }}>
          {mode === 'login' ?'Sign in to your Halo command center' :'Create your hospital intelligence account'}
        </p>
      </div>

      {mode === 'login' ? (
        <form onSubmit={loginForm.handleSubmit(handleLoginSubmit)} className="space-y-4">
          {/* Email */}
          <div>
            <label className="block text-xs font-600 mb-1.5" style={{ color: 'hsl(215 20% 65%)' }}>
              Work Email
            </label>
            <input
              type="email"
              className="input-field"
              placeholder="you@hospital.org"
              {...loginForm.register('email', {
                required: 'Email is required',
                pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email' },
              })}
            />
            {loginForm.formState.errors.email && (
              <p className="text-xs mt-1" style={{ color: 'hsl(0 72% 58%)' }}>
                {loginForm.formState.errors.email.message}
              </p>
            )}
          </div>

          {/* Password */}
          <div>
            <label className="block text-xs font-600 mb-1.5" style={{ color: 'hsl(215 20% 65%)' }}>
              Password
            </label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                className="input-field pr-10"
                placeholder="Your password"
                {...loginForm.register('password', {
                  required: 'Password is required',
                  minLength: { value: 6, message: 'Minimum 6 characters' },
                })}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 p-0.5"
                style={{ color: 'hsl(215 20% 65%)' }}
              >
                {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
              </button>
            </div>
            {loginForm.formState.errors.password && (
              <p className="text-xs mt-1" style={{ color: 'hsl(0 72% 58%)' }}>
                {loginForm.formState.errors.password.message}
              </p>
            )}
          </div>

          {/* Remember + Forgot */}
          <div className="flex items-center justify-between">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                className="w-3.5 h-3.5 rounded accent-emerald-400"
                {...loginForm.register('remember')}
              />
              <span className="text-xs" style={{ color: 'hsl(215 20% 65%)' }}>
                Remember me
              </span>
            </label>
            <button
              type="button"
              className="text-xs font-500 transition-colors hover:opacity-80"
              style={{ color: 'hsl(160 60% 68%)' }}
            >
              Forgot password?
            </button>
          </div>

          {/* Submit */}
          <button
            type="submit"
            disabled={loading}
            className="btn-primary w-full py-3 text-sm flex items-center justify-center gap-2 mt-2 disabled:opacity-60"
            style={{ minHeight: '44px' }}
          >
            {loading ? (
              <>
                <Loader2 size={16} className="animate-spin" />
                Signing in…
              </>
            ) : (
              'Sign In to Halo'
            )}
          </button>
        </form>
      ) : (
        <form onSubmit={signupForm.handleSubmit(handleSignupSubmit)} className="space-y-4">
          {/* Full name */}
          <div>
            <label className="block text-xs font-600 mb-1.5" style={{ color: 'hsl(215 20% 65%)' }}>
              Full Name
            </label>
            <input
              type="text"
              className="input-field"
              placeholder="Dr. Amara Osei"
              {...signupForm.register('fullName', { required: 'Full name is required' })}
            />
            {signupForm.formState.errors.fullName && (
              <p className="text-xs mt-1" style={{ color: 'hsl(0 72% 58%)' }}>
                {signupForm.formState.errors.fullName.message}
              </p>
            )}
          </div>

          {/* Email */}
          <div>
            <label className="block text-xs font-600 mb-1.5" style={{ color: 'hsl(215 20% 65%)' }}>
              Work Email
            </label>
            <input
              type="email"
              className="input-field"
              placeholder="you@hospital.org"
              {...signupForm.register('email', {
                required: 'Email is required',
                pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email' },
              })}
            />
            {signupForm.formState.errors.email && (
              <p className="text-xs mt-1" style={{ color: 'hsl(0 72% 58%)' }}>
                {signupForm.formState.errors.email.message}
              </p>
            )}
          </div>

          {/* Organization */}
          <div>
            <label className="block text-xs font-600 mb-1.5" style={{ color: 'hsl(215 20% 65%)' }}>
              Hospital / Organization
            </label>
            <input
              type="text"
              className="input-field"
              placeholder="National Healthcare Complex"
              {...signupForm.register('organization', { required: 'Organization is required' })}
            />
            {signupForm.formState.errors.organization && (
              <p className="text-xs mt-1" style={{ color: 'hsl(0 72% 58%)' }}>
                {signupForm.formState.errors.organization.message}
              </p>
            )}
          </div>

          {/* Role */}
          <div>
            <label className="block text-xs font-600 mb-1.5" style={{ color: 'hsl(215 20% 65%)' }}>
              Your Role
            </label>
            <div className="relative">
              <select
                className="input-field appearance-none pr-8"
                {...signupForm.register('role', { required: 'Please select a role' })}
              >
                <option value="">Select your role…</option>
                <option value="ceo">Chief Executive Officer (CEO)</option>
                <option value="cfo">Chief Financial Officer (CFO)</option>
                <option value="coo">Chief Operating Officer (COO)</option>
                <option value="moh">Ministry of Health Analyst</option>
                <option value="medical-director">Medical Director</option>
                <option value="other">Other Executive</option>
              </select>
            </div>
            {signupForm.formState.errors.role && (
              <p className="text-xs mt-1" style={{ color: 'hsl(0 72% 58%)' }}>
                {signupForm.formState.errors.role.message}
              </p>
            )}
          </div>

          {/* Password */}
          <div>
            <label className="block text-xs font-600 mb-1.5" style={{ color: 'hsl(215 20% 65%)' }}>
              Password
            </label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                className="input-field pr-10"
                placeholder="Min. 8 characters"
                {...signupForm.register('password', {
                  required: 'Password is required',
                  minLength: { value: 8, message: 'Minimum 8 characters' },
                })}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2"
                style={{ color: 'hsl(215 20% 65%)' }}
              >
                {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
              </button>
            </div>
            {signupForm.formState.errors.password && (
              <p className="text-xs mt-1" style={{ color: 'hsl(0 72% 58%)' }}>
                {signupForm.formState.errors.password.message}
              </p>
            )}
          </div>

          {/* Confirm password */}
          <div>
            <label className="block text-xs font-600 mb-1.5" style={{ color: 'hsl(215 20% 65%)' }}>
              Confirm Password
            </label>
            <div className="relative">
              <input
                type={showConfirm ? 'text' : 'password'}
                className="input-field pr-10"
                placeholder="Repeat password"
                {...signupForm.register('confirmPassword', {
                  required: 'Please confirm your password',
                })}
              />
              <button
                type="button"
                onClick={() => setShowConfirm(!showConfirm)}
                className="absolute right-3 top-1/2 -translate-y-1/2"
                style={{ color: 'hsl(215 20% 65%)' }}
              >
                {showConfirm ? <EyeOff size={15} /> : <Eye size={15} />}
              </button>
            </div>
            {signupForm.formState.errors.confirmPassword && (
              <p className="text-xs mt-1" style={{ color: 'hsl(0 72% 58%)' }}>
                {signupForm.formState.errors.confirmPassword.message}
              </p>
            )}
          </div>

          {/* Terms */}
          <label className="flex items-start gap-2 cursor-pointer">
            <input
              type="checkbox"
              className="w-3.5 h-3.5 rounded mt-0.5 accent-emerald-400"
              {...signupForm.register('terms', { required: 'You must accept the terms' })}
            />
            <span className="text-xs" style={{ color: 'hsl(215 20% 65%)' }}>
              I agree to the{' '}
              <span className="underline cursor-pointer" style={{ color: 'hsl(160 60% 68%)' }}>
                Terms of Service
              </span>{' '}
              and{' '}
              <span className="underline cursor-pointer" style={{ color: 'hsl(160 60% 68%)' }}>
                Privacy Policy
              </span>
            </span>
          </label>
          {signupForm.formState.errors.terms && (
            <p className="text-xs" style={{ color: 'hsl(0 72% 58%)' }}>
              {signupForm.formState.errors.terms.message}
            </p>
          )}

          <button
            type="submit"
            disabled={loading}
            className="btn-primary w-full py-3 text-sm flex items-center justify-center gap-2 disabled:opacity-60"
            style={{ minHeight: '44px' }}
          >
            {loading ? (
              <>
                <Loader2 size={16} className="animate-spin" />
                Creating account…
              </>
            ) : (
              'Create Halo Account'
            )}
          </button>
        </form>
      )}

      {/* Demo credentials */}
      {mode === 'login' && (
        <div
          className="mt-6 rounded-xl border p-4"
          style={{ background: 'hsl(220 33% 12%)', borderColor: 'hsl(220 20% 18%)' }}
        >
          <p className="text-xs font-600 mb-3" style={{ color: 'hsl(215 20% 65%)' }}>
            Demo Accounts — click a row to autofill
          </p>
          <table className="w-full text-xs">
            <thead>
              <tr style={{ borderBottom: '1px solid hsl(220 20% 18%)' }}>
                {['Role', 'Email', 'Password', ''].map((h) => (
                  <th
                    key={`cred-th-${h}`}
                    className="pb-2 text-left font-600"
                    style={{ color: 'hsl(215 15% 45%)' }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {mockCredentials.map((cred) => (
                <tr
                  key={`cred-${cred.role}`}
                  className="cursor-pointer transition-colors hover:bg-white/[0.03]"
                  onClick={() => autofill(cred)}
                  style={{ borderBottom: '1px solid hsl(220 20% 14%)' }}
                >
                  <td className="py-2 pr-3">
                    <span
                      className="px-1.5 py-0.5 rounded font-600 text-xs"
                      style={{ background: 'hsl(160 60% 68% / 0.1)', color: 'hsl(160 60% 68%)' }}
                    >
                      {cred.role}
                    </span>
                  </td>
                  <td className="py-2 pr-3" style={{ color: 'hsl(215 20% 65%)' }}>
                    {cred.email}
                  </td>
                  <td className="py-2 pr-2 font-mono" style={{ color: 'hsl(215 20% 65%)' }}>
                    {cred.password}
                  </td>
                  <td className="py-2">
                    <CopyButton text={cred.password} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}